import { getVisibleScopeSections } from './scopeSheetSchema';
import {
  getBalconyFieldsForGroup,
  getBalconyGroupTitles,
} from './scopeSheetDefaults';
import { titleCaseLabel } from './ScopeField';
import { isFieldVisible } from './scopeFieldVisibility';
import { tipTapDocToPreviewText } from './scopeApiMapper';

const stripHtml = (value = '') =>
  String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatFieldValue = (value, field) => {
  if (value && typeof value === 'object' && value.format === 'tiptap') {
    return tipTapDocToPreviewText(value.doc);
  }
  if (value && typeof value === 'object' && value.type === 'doc') {
    return tipTapDocToPreviewText(value);
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : '';
  if (value === null || value === undefined) return '';
  if (field?.type === 'richNotes' || field?.type === 'notes') {
    return stripHtml(String(value));
  }
  return String(value).trim();
};

const collectFilledFields = (fields = [], values = {}) =>
  fields
    .map((field) => {
      if (!isFieldVisible(field, values)) return null;
      const raw = values?.[field.id];
      const display = formatFieldValue(raw, field);
      if (!display) return null;
      return {
        id: field.id,
        label: titleCaseLabel(field.label),
        value: display,
        isHtml: false,
      };
    })
    .filter(Boolean);

export const buildProjectPreviewSections = (scopeSheet) => {
  const visible = getVisibleScopeSections(scopeSheet);

  return visible
    .map((section) => {
      const sectionData = scopeSheet?.[section.id] || {};

      if (section.type === 'balconyList') {
        const balconies = Array.isArray(sectionData.balconies)
          ? sectionData.balconies
          : [];
        const groupTitles = getBalconyGroupTitles();
        const balconyBlocks = balconies
          .map((balcony, index) => {
            const groups = Object.keys(groupTitles)
              .map((groupId) => {
                const fields = collectFilledFields(
                  getBalconyFieldsForGroup(groupId),
                  balcony?.[groupId] || {}
                );
                if (!fields.length) return null;
                return {
                  id: groupId,
                  title: titleCaseLabel(groupTitles[groupId]),
                  fields,
                };
              })
              .filter(Boolean);

            const label = String(balcony?.label || '').trim();
            if (!groups.length && !label) return null;
            return {
              id: `balcony-${index}`,
              title: label || `Balcony ${index + 1}`,
              groups,
            };
          })
          .filter(Boolean);

        if (!balconyBlocks.length) return null;
        return {
          id: section.id,
          letter: section.letter,
          title: section.title,
          type: 'balconyList',
          balconies: balconyBlocks,
        };
      }

      const subsections = (section.subsections || [])
        .map((subsection) => {
          const fields = collectFilledFields(
            subsection.fields || [],
            sectionData?.[subsection.id] || {}
          );
          if (!fields.length) return null;
          return {
            id: subsection.id,
            title: titleCaseLabel(subsection.title),
            fields,
          };
        })
        .filter(Boolean);

      if (!subsections.length) return null;
      return {
        id: section.id,
        letter: section.letter,
        title: section.title,
        type: 'standard',
        subsections,
      };
    })
    .filter(Boolean);
};
